import { AppSettingContext, LoggingContext, NavigationEntryContext, TripContext } from 'contexts'
import { useContext } from 'react'
import { CompleteStop, OrderItemChangeImage, SyncStatus, TripData } from 'services/types'
import { authentication } from 'utils'
import { AppStateType, FailedRequestType } from 'services/enums'
import { StopDocument } from './types/stop'
import { DbMutation } from 'contexts/db-state'
import { DbOperation } from 'types'
import { AppStateHolder, AppStateLogHolder, CapturedAppStateHolder } from './types/capturedAppStateHolder'
import { SyncingStatus } from './enums/syncingStatus'
import { getDatabaseErrorIfDbContextCannotBeOpened } from 'contexts/db-state/db-worker';
import { useImageUploadService } from './useImageUploadService'
import { useCosmosService } from 'services'

export const useRequestCache = () => {

    const BULK_OPERATIONS_LOG_MAX: number = 10

    const tripContext = useContext(TripContext)
    const navigationContext = useContext(NavigationEntryContext)
    const appSettingContext = useContext(AppSettingContext)
    const dbLogContext = useContext(LoggingContext)
    const { sendImagesToBlobStorage } = useImageUploadService()
    const { getDocuments, upsertDocuments } = useCosmosService()

    const databaseId = 'load_manager'
    const containers = {
        stopCompletion: 'stop_completion',
        appState: 'app_state',
        appStateLogs: 'app_state_logs'
    }

    const getCachedStopCompletion = async (stopId: number): Promise<StopDocument | undefined> => {
        const response = await getDocuments(databaseId, containers.stopCompletion, `SELECT * FROM ${containers.stopCompletion} s WHERE s.id = '${stopId}'`)

        let document: StopDocument | undefined = undefined
        if (response?.length > 0) {
            document = response[0]
        }

        return document
    }

    const cacheStopCompletion = async (stop: CompleteStop) => {
        const existingDocument: StopDocument | undefined = await getCachedStopCompletion(stop?.appointment?.id)

        const now = new Date()
        const documentArray: any[] = [{
            id: existingDocument?.id ?? stop?.appointment?.id?.toString() ?? "0",
            payload: stop,
            createdAt: existingDocument?.createdAt ?? now.toISOString(),
            createdAtMs: existingDocument?.createdAtMs ?? now.getTime(),
            updatedAt: existingDocument ? now.toISOString() : undefined,
            updatedAtMs: existingDocument ? now.getTime() : undefined
        }]        

        await upsertDocuments(databaseId, containers.stopCompletion, documentArray)
    }

    const sendCacheAppState = async (cacheType: AppStateType, comment?: string) => {
        const dbData: TripData = {
            type: cacheType,
            comment: comment,
            currentURL: `${location.pathname}${location.search}`,
            user: authentication.getUser(),
            trips: tripContext.trips.toArray(),
            stops: tripContext.stops.toArray(),
            stopEquipments: tripContext.stopEquipments.toArray(),
            signatures: tripContext.signatures.toArray(),
            appSettings: appSettingContext.appSettings.toArray(),
            returnItems: tripContext.returnItems.toArray(),
            catchWeights: tripContext.catchWeights.toArray(),
            companies: tripContext.companies.toArray(),
            syncStatus: tripContext.syncStatus.toArray(),
            filteredLicensePlates: tripContext.filteredLicensePlates.toArray(),
            licensePlates: tripContext.licensePlates.toArray(),
            licensePlateItems: tripContext.licensePlateItems.toArray(),
            lineItems: tripContext.lineItems.toArray(),
            lineItemBarcodes: tripContext.lineItemBarcodes.toArray(),
            mispicks: tripContext.mispicks.toArray(),
            navigationEntries: navigationContext.navigationEntries.toArray(),
            navigationRoutes: navigationContext.navigationRoutes.toArray(),
            orders: tripContext.orders.toArray(),
            orderItems: tripContext.orderItems.toArray(),
            orderItemChanges: tripContext.orderItemChanges.toArray(),
            orderItemChangeImages: tripContext.orderItemChangeImages.toArray(),
            orderLicensePlates: tripContext.orderLicensePlates.toArray(),
            equipmentShortReasons: tripContext.equipmentShortReasons.toArray(),
            reconcileReasons: tripContext.reconcileReasons.toArray(),
            refusalReasons: tripContext.refusalReasons.toArray(),
            returnChangeReasons: tripContext.returnChangeReasons.toArray(),
            verifyMultipleItemsReasons: tripContext.verifyMultipleItemsReasons.toArray(),
            equipmentTypes: tripContext.equipmentTypes.toArray(),
            migrationErrorMessage: undefined,
            listOfAllTableNames: undefined,
            currentVersionNumber: undefined
        }

        if (cacheType == AppStateType.MigrationIssue) {
            var dbContextError = await getDatabaseErrorIfDbContextCannotBeOpened();

            if (dbContextError != null) {
                dbData.migrationErrorMessage = dbContextError.errorMessage
                dbData.listOfAllTableNames = dbContextError.allTableNames
                dbData.currentVersionNumber = dbContextError.currentVersionNumber
            }
        }

        const now = new Date()
        const appStateId = now.getTime().toString()

         //DB logs can be too large for cosmos. Must be sent separately and have large bulk operation details redacted
         const dbLogs: DbOperation[] = dbLogContext.dbOperations.map(log => {
            const isBulkOperation: boolean = [DbMutation.Set, DbMutation.BulkPut, DbMutation.BulkRemove].includes(log.operation)
            return {
                ...log,
                items: (isBulkOperation && log.items?.length > BULK_OPERATIONS_LOG_MAX) ? `<${log.items?.length} items>` : log.items
            }
        })

        if (navigator.onLine) {
            var orderItemChangeImages = tripContext.orderItemChangeImages.toArray();

            if (orderItemChangeImages != undefined && orderItemChangeImages.length > 0) {
                let blobStorageUploadFileResults = await sendImagesToBlobStorage(orderItemChangeImages);

                let orderItemChangeImagesDeepCopies: OrderItemChangeImage[] = []
                
                for (let index = 0; index < blobStorageUploadFileResults.length; index++) {
                    orderItemChangeImages[index].filenameInBlobStorage = blobStorageUploadFileResults[index].filename

                    let orderItemChangeImagesDeepCopy: OrderItemChangeImage = JSON.parse(JSON.stringify(orderItemChangeImages[index]))
                    orderItemChangeImagesDeepCopy.file = "";

                    orderItemChangeImagesDeepCopies.push(orderItemChangeImagesDeepCopy)
                }

                tripContext.orderItemChangeImages.bulkPut(orderItemChangeImages)

                dbData.orderItemChangeImages = orderItemChangeImagesDeepCopies
            }

            const appStateDocumentArray: any[] = [{
                id: appStateId,
                payload: dbData,
                createdAt: now.toISOString(),
                createdAtMs: now.getTime()
            }]

            await upsertDocuments(databaseId, containers.appState, appStateDocumentArray)

            const appStateLogDocumentArray: any[] = [{
                id: now.getTime().toString(),
                appStateId: appStateId,
                payload: {
                    dbOperations: dbLogs
                },
                createdAt: now.toISOString(),
                createdAtMs: now.getTime(),
            }]
            await upsertDocuments(databaseId, containers.appStateLogs, appStateLogDocumentArray)
        }

        else {
            dbData.orderItemChangeImages = tripContext.orderItemChangeImages.toArray()

            var appStateHolder: AppStateHolder = {
                appState: containers.appState,
                id: appStateId,
                payload: dbData,
                createdAt: now.toISOString(),
                createdAtMs: now.getTime()
            };

            var appStateLogHolder: AppStateLogHolder = {
                id: now.getTime().toString(),
                appStateId: appStateId,
                dbOperations: dbLogs,
                createdAt: now.toISOString(),
                createdAtMs: now.getTime(),
            }

            var capturedAppStateHolder: CapturedAppStateHolder = {
                id: new Date().getTime(),
                appStateHolder: appStateHolder,
                appStateLogHolder: appStateLogHolder
            }

            tripContext.syncStatus.put({id: capturedAppStateHolder.id!, data: capturedAppStateHolder,
                status: SyncingStatus.FailedToSync, type: FailedRequestType.SendAppState})
        }
    }

    const sendCapturedAppStateHoldersToAzure = async (): Promise<boolean> => {

        var capturedAppStates = tripContext.syncStatus.filter(request => request.status == SyncingStatus.FailedToSync && request.type == FailedRequestType.SendAppState);        
        const capturedAppStateHolderIds: number[] = capturedAppStates.map(x => x.id) ?? [];

        for (const capturedAppStateHolderId of capturedAppStateHolderIds) {

            const capturedAppStateHolderSyncStatus: SyncStatus = capturedAppStates.find(x => x.id == capturedAppStateHolderId)!;
            const capturedAppStateHolder: CapturedAppStateHolder = capturedAppStateHolderSyncStatus.data as CapturedAppStateHolder;

            try {

                var orderItemChangeImages = capturedAppStateHolder.appStateHolder.payload.orderItemChangeImages;

                if (orderItemChangeImages != undefined && orderItemChangeImages.length > 0) {

                    let blobStorageUploadFileResults = await sendImagesToBlobStorage(orderItemChangeImages);

                    if (blobStorageUploadFileResults.length == 0)
                        return false

                    let orderItemChangeImagesDeepCopy: OrderItemChangeImage[] = JSON.parse(JSON.stringify(orderItemChangeImages))

                    for (let index = 0; index < blobStorageUploadFileResults.length; index++) {
                        orderItemChangeImagesDeepCopy[index].filenameInBlobStorage = blobStorageUploadFileResults[index].filename
                        orderItemChangeImagesDeepCopy[index].file = ""
                    }

                    capturedAppStateHolder.appStateHolder.payload.orderItemChangeImages = orderItemChangeImagesDeepCopy
                }

                const appStateDocumentArray: any[] = [{
                    id: capturedAppStateHolder.appStateHolder.id,
                    payload: capturedAppStateHolder.appStateHolder.payload,
                    createdAt: capturedAppStateHolder.appStateHolder.createdAt,
                    createdAtMs: capturedAppStateHolder.appStateHolder.createdAtMs,
                }]
                await upsertDocuments(databaseId, containers.appState, appStateDocumentArray)

            } catch (e) {
                setCapturedAppStateHolderSyncStatusToFailedToSync(capturedAppStateHolderId);

                return false;
            }
    
            try {
                const appStateLogDocumentArray: any[] = [{
                    id: capturedAppStateHolder.appStateHolder.id,
                    payload: capturedAppStateHolder.appStateHolder.payload,
                    createdAt: capturedAppStateHolder.appStateHolder.createdAt,
                    createdAtMs: capturedAppStateHolder.appStateHolder.createdAtMs,
                }]
                await upsertDocuments(databaseId, containers.appStateLogs, appStateLogDocumentArray)

            } catch (e) {
                setCapturedAppStateHolderSyncStatusToFailedToSync(capturedAppStateHolderId);

                return false;
            }

            tripContext.syncStatus.remove(capturedAppStateHolderSyncStatus.id)
          }

        return true;
    }

    const setCapturedAppStateHolderSyncStatusToFailedToSync = (capturedAppStateHolderId: number): void => {
        const capturedAppStateHolderSyncStatus: SyncStatus = tripContext.syncStatus.find(request =>
            request.status == SyncingStatus.FailedToSync && request.type == FailedRequestType.SendAppState &&
            request.id == capturedAppStateHolderId)!;

        capturedAppStateHolderSyncStatus.status = SyncingStatus.FailedToSync;

        tripContext.syncStatus.put(capturedAppStateHolderSyncStatus);
    }

    return {
        getCachedStopCompletion,
        cacheStopCompletion,
        sendCacheAppState,
        sendCapturedAppStateHoldersToAzure
    }
}